import {
  grantsFreeMagnets, grantsFreeItem, freeAddonsOf, freeItemLabel, freeItemNoun,
} from '../../utils/offerPricing';
import { formatDeadline } from '../../utils/offerDeadline';
import { popularityComparator } from '../_pages/Holiday/Builder/popularityOrder';
import { ORDER_TYPES } from '../_pages/Holiday/Builder/orderType';
import { selectableDesigns } from '../_pages/FreeItem/freeItemPage';
import { hasArtwork } from '../../utils/optionArtwork';

// What a package card shows for the free-magnets tier (issue #5218 items 7 and
// 9). Pure functions: every surface asks grantsFreeMagnets (the ONE
// interpreter) through these, so the badge, the checklist row, the magnet slot
// and the a-la-carte label appear and vanish together with the offer.

export const FREE_MAGNETS_BADGE = '+2 FREE MAGNETS';

// The badge text, or null when the tier is not live.
export const freeMagnetsBadge = (offer) => (grantsFreeMagnets(offer) ? FREE_MAGNETS_BADGE : null);

// The checklist row appended under the option's own contents:
// "(2) FREE Photo Magnets — offer ends Friday, October 24". Null when not live.
export function freeMagnetsRow(offer) {
  if (!grantsFreeMagnets(offer)) return null;
  const deadline = formatDeadline(offer.expiresOn);
  return `(2) FREE Photo Magnets${deadline ? ` — offer ends ${deadline}` : ''}`;
}

// The a-la-carte "Magnets" option on the pricelist (778 has one at $14.95;
// 877 needs one added in admin). Matched by name, the same way the checklist
// parser recognises a magnet row.
export const isMagnetsOption = (option) => /magnet/i.test(option?.name || '');

// The label that replaces the Magnets option's tagline while the tier is live.
export const magnetsOptionLabel = (option, offer) => (
  grantsFreeMagnets(offer) && isMagnetsOption(option) ? 'FREE with any package' : null
);

// ---- Free item of your choice (#5747) ---------------------------------------
//
// What a package card shows while a FREE_ADDON_CHOICE offer is live. Same
// shape as the magnet tier's three above, keyed off grantsFreeItem (the ONE
// interpreter), so badge, row and thumbnail appear and vanish together.

// The ribbon: "+ FREE MAGNET" -- the set's category noun, because the full
// label can run five words and a ribbon has room for one.
export const freeItemBadge = (offer) => (grantsFreeItem(offer) ? `+ FREE ${freeItemNoun(offer).toUpperCase()}` : null);

// The checklist row appended under the option's own contents:
// "Includes a FREE dry erase magnet with package". Just that (Katie,
// 2026-10-18): no design count and no deadline here -- the banner above the
// cards carries the deadline, and the picker is where the designs are.
// Null when not live.
export function freeItemRow(offer) {
  if (!grantsFreeItem(offer)) return null;
  return `Includes a FREE ${freeItemLabel(offer)} with package`;
}

// The picture of the free item on a package card (#5747): WHICH add-on of the
// offer's set, and WHICH of this child's photos to composite into it.
//
// Which add-on: the MOST POPULAR one (Katie, 2026-10-22) -- the gifts shelf's
// "Most Popular" sort for this order type (popularityComparator, the ONE
// definition; the server serves the set in the shelf's own order, so ties fall
// as the shelf lays them out). Only ever among the add-ons the coupon names
// as free. Which photo: the first the design can hold -- selectableDesigns,
// the free-item picker's own projection over poseEligibility (the ONE rule
// every surface that draws a pose on a product applies). A design this child
// has no usable photo for is passed over for the next most popular that has
// -- the picker will not offer it either. With no usable photo for any of
// them, the most popular one's plain product photo stands in (`pose` null),
// as it did before compositing.
//
// `canComposite(addon)` is the CALLER's drawability test (the gifts renderer's
// own, canRenderPreview + giftRenderable, on the card); it defaults to "has
// product art" so a caller with no renderer gets the pre-compositing answer.
// `value` is the pictured item's price, so the picture and the number never
// disagree; null when it has no price to quote. Returns null when the set has
// nothing drawable at all (the row still says it in words).
export function freeItemThumbnail(offer, {
  orderType = ORDER_TYPES.UNKNOWN, poses = [], orientations = {}, canComposite = hasArtwork,
} = {}) {
  if (!grantsFreeItem(offer)) return null;
  const byPopularity = [...freeAddonsOf(offer)].sort(popularityComparator(orderType));
  const [first] = selectableDesigns(byPopularity.filter(canComposite), poses, orientations);
  const plain = byPopularity.find(hasArtwork);
  const chosen = first ? { addon: first.addon, pose: first.poses[0] } : (plain && { addon: plain, pose: null });
  if (!chosen) return null;
  const { addon, pose } = chosen;
  const price = Number(addon.priceWith);
  return {
    addon, pose, src: addon.imageUrl, alt: addon.name || '', value: price > 0 ? price : null,
  };
}
